import { useState, useCallback } from "react";
import type { CategoryNode, StyleTag } from "../types";
import { useAutoRowManagement } from "./useAutoRowManagement";

interface BulkStyleRow {
  id: string;
  name: string;
  styleNumber: string;
  images: File[];
  tags: StyleTag[];
}

interface UseBulkStyleCreationReturn {
  category: CategoryNode | null;
  rows: BulkStyleRow[];
  setCategory: (category: CategoryNode | null) => void;
  addRow: () => void;
  removeRow: (id: string) => void;
  updateRow: <K extends keyof BulkStyleRow>(
    id: string,
    field: K,
    value: BulkStyleRow[K]
  ) => void;
  clearAll: () => void;
  validateRows: () => boolean;
  getFormDataArray: () => FormData[];
}

// Helper function to check if a row has any content (DRY principle)
const isRowActive = (row: BulkStyleRow): boolean => {
  return !!(
    row.name.trim() ||
    row.styleNumber.trim() ||
    row.images.length > 0 ||
    row.tags.length > 0
  );
};

export const useBulkStyleCreation = (): UseBulkStyleCreationReturn => {
  const [category, setCategory] = useState<CategoryNode | null>(null);
  const [rows, setRows] = useState<BulkStyleRow[]>([]);

  const createNewRow = (): BulkStyleRow => ({
    id: Math.random().toString(36).substr(2, 9),
    name: "",
    styleNumber: "",
    images: [],
    tags: [],
  });

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, createNewRow()]);
  }, []);

  // Initialize auto row management
  useAutoRowManagement({
    rows,
    addRow,
    isRowActive,
  });

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const updateRow = useCallback(
    <K extends keyof BulkStyleRow>(
      id: string,
      field: K,
      value: BulkStyleRow[K]
    ) => {
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
      );
    },
    []
  );

  const clearAll = useCallback(() => {
    // setCategory(null);
    setRows([]);
  }, []);

  const validateRows = useCallback(() => {
    if (!category) return false;
    return rows
      .slice(0, -1)
      .every(
        (row) =>
          row.name.trim() && row.styleNumber.trim() && row.images.length > 0
      );
  }, [category, rows]);

  const getFormDataArray = useCallback((): FormData[] => {
    return rows.slice(0, -1).map((row) => {
      const formData = new FormData();

      // Add text fields
      formData.append("name", row.name);
      formData.append("styleNumber", row.styleNumber);

      // Add category data
      if (category) {
        formData.append("category", JSON.stringify(category));
      }

      // Add tags as JSON or individual entries
      // row.tags.forEach((tag, index) => {
      //   formData.append(`tags[${index}]`, tag.tag_id);
      // });

      formData.append(
        "tags",
        JSON.stringify(row.tags.map((tag) => tag.tag_id || tag))
      );

      // Add images as binary data
      // row.images.forEach((file, index) => {
      //   if (index === 0) {
      //     // First image is cover image
      //     formData.append("coverImage", file);
      //   } else {
      //     // Other images
      //     formData.append("images", file);
      //   }
      // });

      formData.append("coverImage", row.images[0]);
      formData.append("images", JSON.stringify(row.images.map((file) => file)));

      return formData;
    });
  }, [rows, category]);

  return {
    category,
    rows,
    setCategory,
    addRow,
    removeRow,
    updateRow,
    clearAll,
    validateRows,
    getFormDataArray,
  };
};
